
import React, { useState, useEffect } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import SceneIdeasManager from "@/components/admin/tools/SceneIdeasManager";
import EmbeddableSceneGenerator from "@/components/tools/EmbeddableSceneGenerator";
import { db } from "@/integrations/cloudflare/client";

const SceneGeneratorConfig = () => {
  const { isLoading: isAuthLoading } = useAdminCheck();
  const [sceneCount, setSceneCount] = useState<number>(0);
  const [isLoadingCount, setIsLoadingCount] = useState(true);

  useEffect(() => {
    const loadSceneCount = async () => {
      try {
        const { count, error } = await db
          .from("elf_scenes")
          .select("*", { count: "exact", head: true });
        
        if (error) throw error;
        setSceneCount(count || 0);
      } catch (error) {
        console.error("Error loading scene count:", error);
      } finally {
        setIsLoadingCount(false);
      }
    };

    loadSceneCount();
  }, []);

  return (
    <AdminLayout 
      title="Scene Generator Configuration" 
      backLink="/admin/tools" 
      backLabel="Back to Tools"
    >
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Holiday Elf Scene Generator</h1>
        <p className="text-gray-500">
          Configure and manage the interactive scene generator tool that can be embedded in blog posts.
        </p>
      </div>

      <Tabs defaultValue="scenes">
        <TabsList className="mb-4">
          <TabsTrigger value="scenes">Scene Ideas</TabsTrigger>
          <TabsTrigger value="preview">Preview Tool</TabsTrigger>
        </TabsList>
        
        <TabsContent value="scenes">
          <SceneIdeasManager />
        </TabsContent>
        
        <TabsContent value="preview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Default Theme</CardTitle>
                <CardDescription>
                  Preview how the scene generator appears with the default theme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EmbeddableSceneGenerator theme="default" />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Christmas Theme</CardTitle>
                <CardDescription>
                  Preview how the scene generator appears with the Christmas theme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EmbeddableSceneGenerator theme="christmas" />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Minimal Theme</CardTitle>
                <CardDescription>
                  Preview how the scene generator appears with the minimal theme
                </CardDescription>
              </CardHeader>
              <CardContent>
                <EmbeddableSceneGenerator theme="minimal" />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default SceneGeneratorConfig;
